import type { BlockState, ChatBlock, Message, Ticket } from '@/api/support';

/**
 * Pure rules of the chat cards (CONCIERGE_CHAT_FIRST.md §5.2): which text a card shows, whether it still asks the
 * client for something, how a poll merges into the feed. The component only renders what these return.
 */

const KNOWN_EVENTS = new Set([
  'RECEIVED', 'OPENED_BY_MANAGER', 'NEEDS_INFO', 'REJECTED', 'PAYMENT_CREATED', 'CLIENT_MARKED_SENT',
  'PAYMENT_NEEDS_INFO', 'FUNDS_RECEIVED', 'ON_HOLD', 'SENT', 'PAYMENT_FAILED', 'PAYMENT_RESET', 'REPAYMENT_ALLOWED',
  'REFUND_PENDING', 'REFUND_BOUNCED', 'REFUND_DONE', 'CLOSED', 'CLOSED_STALE', 'CANCELLED',
]);

/**
 * The i18n key of a concierge system line (§5.2 «Событие → строка в переписке»). A failed payment reads differently
 * when TH cancelled it; an event this build does not know is the plain «Статус задачи обновлён».
 */
/** The task's opening lines — the concierge greets the client before them (owner 01.11: «Где здравствуйте?»). */
const GREETED_EVENTS = new Set(['RECEIVED', 'OPENED_BY_MANAGER']);
export function greets(event: string): boolean {
  return GREETED_EVENTS.has(event);
}

export function eventKey(block: Extract<ChatBlock, { type: 'CONCIERGE_EVENT' }>): string {
  if (block.event === 'PAYMENT_FAILED' && block.failure === 'CANCELLED') {
    return 'vitrine.concierge.event.PAYMENT_FAILED_CANCELLED';
  }
  return `vitrine.concierge.event.${KNOWN_EVENTS.has(block.event) ? block.event : 'UNKNOWN'}`;
}

/** A card this build does not know is not shown at all — never a raw type on the client's screen. */
const KNOWN_BLOCKS = new Set<ChatBlock['type']>([
  'CONCIERGE_TERMS', 'CONCIERGE_PAY_IN', 'CONCIERGE_EVENT', 'CONCIERGE_REFUND_TO', 'REQUEST_DOCUMENT', 'QUESTION',
  'REQUEST_INPUT', 'MONEY_REQUEST', 'IDENTITY_VOUCH', 'TOPUP_REQUEST', 'TOPUP_PAY_IN', 'TOPUP_EVENT',
  'CARD_ACTION',
]);

export function isRenderableBlock(m: Message): boolean {
  return m.kind === 'BLOCK' && !!m.block && KNOWN_BLOCKS.has(m.block.type);
}

/**
 * The card still waits for the client: a question not answered, a field not filled, a document not sent. Without a
 * state (an older server, or the poll has not brought it yet) the card is open — the server refuses a stale answer.
 */
export function awaitsClient(state: BlockState | undefined): boolean {
  if (!state) return true;
  switch (state.type) {
    case 'QUESTION':
    case 'REQUEST_INPUT':
      return !state.answered;
    case 'REQUEST_DOCUMENT':
      return !state.fulfilled;
    case 'IDENTITY_VOUCH':
      return !!state.vouch?.canAnswer;
    // The requisites wait for «Я отправил» while they are the current ones and the request still takes money.
    case 'TOPUP_PAY_IN':
      return !state.superseded && !!state.request?.payInInstructions && !state.request.markedSent;
    default:
      return false;
  }
}

/**
 * A card whose state moves on the server without a new message in the thread: a money request waiting for the payer
 * or on its way to the card («Разрешено · отправляем» → «Зачислено»). While one is on screen the chat keeps polling at
 * its shortest interval instead of backing off to a minute — the client is watching that chip.
 */
export function hasMovingBlock(states: Record<string, BlockState>): boolean {
  return Object.values(states).some(s => s.type === 'MONEY_REQUEST'
    && (s.request.state === 'PENDING' || s.request.state === 'APPROVED'));
}

/** A payment card (requisites or refund details) whose payment is no longer the current one — «Реквизиты заменены». */
export function isSuperseded(state: BlockState | undefined): boolean {
  return (state?.type === 'CONCIERGE_PAY_IN' || state?.type === 'CONCIERGE_REFUND_TO' || state?.type === 'TOPUP_PAY_IN')
    && state.superseded;
}

const TOPUP_EVENTS = new Set(['MARKED_SENT', 'FUNDS_RECEIVED', 'COMPLETED', 'ON_HOLD', 'EXPIRED', 'CLOSED']);

/**
 * The i18n key of a top-up status line (docs/cabinet-redesign/02 §5.1 В-3); cash reads in its own words where they
 * differ («передали наличные»). An event this build does not know is the plain «Статус пополнения обновлён».
 */
export function topUpEventKey(event: string, sourceKind: string | null | undefined): string {
  if (!TOPUP_EVENTS.has(event)) return 'cabinet.topupChat.event.UNKNOWN';
  if (event === 'MARKED_SENT' && sourceKind === 'CASH') return 'cabinet.topupChat.event.MARKED_SENT_CASH';
  return `cabinet.topupChat.event.${event}`;
}

/**
 * The line of a superseded payment card: «Оплата отменена, средства возвращены» when its payment was refunded,
 * otherwise «Реквизиты заменены» (replaced by a new payment).
 */
export function supersededKey(state: { refunded: boolean }): string {
  return state.refunded ? 'vitrine.concierge.payin.refunded' : 'vitrine.concierge.payin.superseded';
}

/**
 * The terms card's state line (§5.2, four states). CURRENT has none — its buttons come with stage 3. CLOSED is
 * «Задача закрыта» on a finished task and «Менеджер обновит условия» while the terms are being redone.
 */
export function termsStateKey(state: BlockState | undefined, taskClosed: boolean): string | null {
  if (state?.type !== 'CONCIERGE_TERMS') return null;
  switch (state.state) {
    case 'SUPERSEDED': return 'vitrine.concierge.terms.state.superseded';
    case 'CONFIRMED': return 'vitrine.concierge.terms.state.confirmed';
    case 'CLOSED': return taskClosed ? 'vitrine.concierge.terms.state.closed' : 'vitrine.concierge.terms.state.updating';
    default: return null;
  }
}

/** One minute back from the newest shown message (§5.2): a message committed late with an earlier timestamp is not lost. */
export const POLL_OVERLAP_MS = 60_000;
const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

/** The poll cursor: `afterCreatedAt = last − 60 s`, zero `afterId`; undefined — read the whole thread. */
export function overlapCursor(last: Pick<Message, 'createdAt'> | undefined): { createdAt: string; id: string } | undefined {
  if (!last) return undefined;
  const at = Date.parse(last.createdAt);
  if (!Number.isFinite(at)) return undefined;
  return { createdAt: new Date(at - POLL_OVERLAP_MS).toISOString(), id: ZERO_UUID };
}

/** What a poll brings that is not on screen yet, in the server's order. */
export function unseen(shown: readonly Pick<Message, 'id'>[], page: readonly Message[]): Message[] {
  const ids = new Set(shown.map(m => m.id));
  return page.filter(m => !ids.has(m.id));
}

/** Concierge task threads live on the task's screen, not in the general chat (§5.2 «Непрочитанное», CF-021). */
export function isConciergeThread(th: Pick<Ticket, 'refType'>): boolean {
  return th.refType === 'CONCIERGE';
}
